import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * Layout mode:
   * - 'standalone' (default): Card with background and padding
   * - 'integrated': Minimal, blends with parent container
   */
  mode?: 'standalone' | 'integrated';
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  mode = 'standalone',
  className = '',
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const paginationContent = (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`p-2 rounded-lg transition-colors ${
          currentPage > 1
            ? 'bg-gray-700 hover:bg-gray-800 text-white shadow-sm cursor-pointer'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
        aria-label="Previous page"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg">
        <span
          className="text-sm font-semibold text-gray-700"
          aria-current="page"
          aria-label={`Page ${currentPage} of ${totalPages}, current page`}
        >
          Page {currentPage} of {totalPages}
        </span>
      </div>

      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-lg transition-colors ${
          currentPage < totalPages
            ? 'bg-gray-700 hover:bg-gray-800 text-white shadow-sm cursor-pointer'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
        aria-label="Next page"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );

  // Standalone mode: wrapped in card
  if (mode === 'standalone') {
    return (
      <div className={className}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50">{paginationContent}</div>
        </div>
      </div>
    );
  }

  // Integrated mode: just the controls
  return <div className={className}>{paginationContent}</div>;
}
